import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

const MENU_WIDTH = 176;
const GAP = 4;

// The "…" menu on a Teams member row. The members table sits in a rounded
// card with overflow:hidden, which clipped an absolutely-positioned menu on
// the last row, so the menu goes to document.body at a fixed position under
// the button instead. It flips upward when there is no room below.
export default function TeamMemberRowMenu({ open, onToggle, onClose, children, T }) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) { setPos(null); return; }
    const r = buttonRef.current.getBoundingClientRect();
    const h = menuRef.current?.offsetHeight || 0;
    const below = r.bottom + GAP;
    const top = below + h > window.innerHeight && r.top - GAP - h > 0 ? r.top - GAP - h : below;
    setPos({ top, left: Math.max(8, r.right - MENU_WIDTH) });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = e => {
      if (menuRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      onClose();
    };
    // A fixed menu would drift away from its row, so any scroll closes it.
    const close = () => onClose();
    document.addEventListener('mousedown', outside);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', outside);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, onClose]);

  return (
    <>
      <button ref={buttonRef} type="button" onClick={onToggle} aria-haspopup="menu" aria-expanded={open}
        style={{borderRadius:8, padding:6, color:T.textMuted,
          background:'none', border:'none', cursor:'pointer', lineHeight:1}}>
        <MoreHorizontal size={14}/>
      </button>
      {open && createPortal(
        <div ref={menuRef} role="menu" style={{
          position:'fixed', top:pos?.top ?? 0, left:pos?.left ?? 0,
          visibility: pos ? 'visible' : 'hidden', zIndex:1000, width:MENU_WIDTH,
          borderRadius:12, border:`1px solid ${T.border}`, background:T.card,
          boxShadow:T.shadow, padding:'4px 0', textAlign:'left',
        }}>
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
